import * as React from 'react';
import { useState, useEffect, useCallback } from 'react';

/** Material UI Packages. */
import { Box } from '@mui/material';
import { randomId } from '@mui/x-data-grid-generator';

/** Auth0 authenticator Packages. */
import { useAuth0 } from '@auth0/auth0-react';

/** Redux Packages. */
import { useDispatch } from 'react-redux';

/** Redux Reducers. */
import { setSnackbar } from 'shared/store/slices/snackBarSlice';

/** Internal Components. */
import { CustomSkeleton2 } from 'shared/ui/Skeleton';
import { MainDatagrid } from 'shared/ui/Datagrid';
import { callAPI } from 'shared/api/callApi';

import ClientDialog from './ClientDialog';
import { teamCol, userCol } from '../lib/ClientSettingDef';

export default function ClientDataGrid(props) {
    /** Get props from ClientSetting.js */
    const {
        type = 'team', // 'team' or 'client'
        loaded,
        setLoaded,
        tabValue,
        teamSettingRows,
        setTeamSettingRows,
        clientAssignmentRows,
        setClientAssignmentRows,
        tradePartner,
        setTradePartner,
        // Client assignment specific props
        teamList,
        setTeamList,
        subClientList,
        setSubClientList,
        subClientRows,
        setSubClientRows,
        subClientDialogOpen,
        setSubClientDialogOpen,
    } = props;

    /** Auth0 user and token */
    const { user, getAccessTokenSilently } = useAuth0();

    /** Get the dispatch function from Redux to send actions to the store. */
    const dispatch = useDispatch();

    /** State for managing client dialog. */
    const [dialogOpen, setDialogOpen] = useState(false);
    /** ID value used to identify which column triggered the dialog in the DataGrid. */
    const [selectedId, setSelectedId] = useState(null);
    /** Array of IDs representing the rows that are already selected within the DataGrid inside the dialog. */
    const [rowSelectionModel, setRowSelectionModel] = useState([]);

    /**
     * Sort trade partners by name.
     * @param {Array} tradePartners - Array of trade partners
     * @returns {Array} Sorted trade partners
     */
    const sortTradePartner = useCallback((tradePartners) => {
        return [...tradePartners].sort((a, b) => a.name.localeCompare(b.name));
    }, []);

    /** Fetch API data based on type. */
    useEffect(() => {
        const config =
            type === 'team'
                ? {
                      method: 'get',
                      module: 'client_setting',
                      component: 'team_setting',
                      onSuccess: (resp) => {
                          setTeamSettingRows(resp.data.il_team);
                          setTradePartner(sortTradePartner(resp.data.il_trade_partner));
                      },
                  }
                : {
                      method: 'get',
                      module: 'client_setting',
                      component: 'client_assignments',
                      onSuccess: (resp) => {
                          setTeamList(resp.data.il_team);
                          setClientAssignmentRows(resp.data.il_user);
                          setTradePartner(sortTradePartner(resp.data.il_trade_partner));
                          setSubClientList(resp.data.il_subclient_assignment);
                      },
                  };

        callAPI({
            ...config,
            getAccessTokenSilently,
            email: user.email,
            onError: () =>
                dispatch(
                    setSnackbar({
                        open: true,
                        message: 'Something went wrong, please try again.',
                        severity: 'error',
                    }),
                ),
            onFinal: () => setLoaded(true),
        });
    }, [getAccessTokenSilently, user.email, type, tabValue]);

    /** Update user rows with subclients (only for client type). */
    useEffect(() => {
        if (type === 'client') {
            const result = clientAssignmentRows.map((row) => {
                const existingItem = subClientList
                    .filter((item) => row.id === item.il_user_id)
                    .map((val) => val.il_trade_partner_id_shipper)
                    .flat();
                const uniqueItem = [...new Set(existingItem)];
                return {
                    ...row,
                    subclient: uniqueItem || [],
                };
            });
            setClientAssignmentRows(result);
        }
    }, [type, tabValue, subClientList]);

    /**
     * Process row update in datagrid.
     * @param {object} newRow - Updated row data
     * @returns {object} Updated row data
     */
    const handleProcessRowUpdate = useCallback(
        (newRow) => {
            if (type === 'team') {
                const updatedRow = { ...newRow, isNew: false };
                return new Promise((resolve, reject) => {
                    callAPI({
                        method: 'put',
                        module: 'client_setting',
                        component: 'team_setting',
                        getAccessTokenSilently,
                        email: user.email,
                        obj: JSON.stringify(newRow),
                        onSuccess: (resp) => {
                            setTeamSettingRows(teamSettingRows.map((row) => (row.id === newRow.id ? updatedRow : row)));
                            dispatch(
                                setSnackbar({
                                    open: true,
                                    message: 'Successfully updated.',
                                    severity: 'success',
                                }),
                            );
                            resolve(updatedRow);
                        },
                        onError: (error) => {
                            dispatch(
                                setSnackbar({
                                    open: true,
                                    message: 'Something went wrong, please try again.',
                                    severity: 'error',
                                }),
                            );
                            reject(error);
                        },
                    });
                });
            } else {
                const findClient = teamList
                    .filter((item) => newRow.il_team_id.includes(item.id))
                    .flatMap((item) => item.il_trade_partner_id);
                const uniqueClients = [...new Set(findClient)];
                const filteredRow = {
                    ...newRow,
                    il_trade_partner_id: newRow.il_trade_partner_id.filter((id) => uniqueClients.includes(id)),
                };
                return new Promise((resolve, reject) => {
                    callAPI({
                        method: 'put',
                        module: 'client_setting',
                        component: 'client_assignments',
                        getAccessTokenSilently,
                        email: user.email,
                        obj: JSON.stringify(filteredRow),
                        onSuccess: (resp) => {
                            const newRow = resp.data.il_user;
                            const newSubClient = resp.data.il_subclient_assignment;
                            setClientAssignmentRows((prevRows) =>
                                prevRows.map((item) => (item.id === newRow.id ? newRow : item)),
                            );
                            setSubClientRows((prevRows) =>
                                prevRows.map((item) => (item.id === newSubClient.id ? newSubClient : item)),
                            );
                            dispatch(
                                setSnackbar({
                                    open: true,
                                    message: 'Successfully updated.',
                                    severity: 'success',
                                }),
                            );
                            resolve(newRow || filteredRow);
                        },
                        onError: () =>
                            dispatch(
                                setSnackbar({
                                    open: true,
                                    message: 'Something went wrong, please try again.',
                                    severity: 'error',
                                }),
                            ),
                    });
                });
            }
        },
        [getAccessTokenSilently, user.email, type, tabValue, teamList, teamSettingRows, clientAssignmentRows],
    );

    /**
     * Get team content by ID
     * @param {string} id - Team ID
     * @returns {string} Team name
     */
    const getContentById_team = useCallback(
        (id) => {
            return teamList?.find((option) => option.id === id)?.name || '';
        },
        [teamList],
    );

    /**
     * Get client content by ID
     * @param {string} id - Client ID
     * @returns {string} Client name
     */
    const getContentById_client = useCallback(
        (id) => {
            return tradePartner?.find((option) => option.id === id)?.name || '';
        },
        [tradePartner],
    );

    /**
     * Open dialog with corresponding data
     * @param {string} id - Row ID
     */
    const handleOpenMainDialog = useCallback(
        (id) => {
            if (type === 'client') {
                const selectedTeam = clientAssignmentRows.find((item) => item.id === id)?.il_team_id;
                if (selectedTeam?.length) {
                    setDialogOpen(true);
                    setSelectedId(id);
                } else {
                    alert('Please assign at least one team.');
                }
            } else {
                setSelectedId(id);
                setDialogOpen(true);
            }
        },
        [type, clientAssignmentRows],
    );

    /**
     * Open subclient dialog with corresponding data
     * @param {string} id - User ID
     */
    const handleOpenSubDialog = useCallback(
        (id) => {
            const filteredSubClientList = subClientList.filter((item) => item.il_user_id === id);
            const findUser = clientAssignmentRows.find((item) => item.id === id)?.il_trade_partner_id || [];
            const findForwarder = tradePartner.filter(
                (partner) => findUser.includes(partner.id) && partner.partner_type === 'Forwarder',
            );

            const result = findForwarder.map((forwarder) => {
                const existingItem = filteredSubClientList.find(
                    (item) => item.il_trade_partner_id_forwarder.id === forwarder.id,
                );

                if (existingItem) {
                    return {
                        id: randomId(),
                        ...existingItem,
                    };
                } else {
                    return {
                        id: randomId(),
                        il_trade_partner_id_forwarder: forwarder,
                        il_trade_partner_id_shipper: [],
                        il_user_id: filteredSubClientList.length > 0 ? filteredSubClientList[0]?.il_user_id : id,
                    };
                }
            });
            setSubClientRows(result);
            setSubClientDialogOpen(true);
        },
        [subClientList, clientAssignmentRows, tradePartner],
    );

    /** Get columns based on type */
    const getColumns = () => {
        if (type === 'team') {
            return teamCol(handleOpenMainDialog, tradePartner);
        } else {
            return userCol(
                handleOpenMainDialog,
                handleOpenSubDialog,
                teamList,
                getContentById_team,
                getContentById_client,
            );
        }
    };

    /** Get rows based on type */
    const getRows = () => {
        return type === 'team' ? teamSettingRows : clientAssignmentRows;
    };

    return (
        <Box sx={{ my: 1 }}>
            {loaded ? (
                <MainDatagrid
                    rows={getRows()}
                    cols={getColumns()}
                    processRowUpdate={handleProcessRowUpdate}
                    additional={{ disableColumnMenu: true }}
                    sx={{
                        fontSize: 12,
                        height: '79vh',
                        p: 0,
                        width: '100%',
                        '& .MuiDataGrid-main': {
                            borderRadius: 2,
                            boxShadow: 2,
                        },
                    }}
                />
            ) : (
                <CustomSkeleton2 />
            )}

            {/* Client Dialog */}
            <ClientDialog
                type="client"
                tabValue={tabValue}
                teamSettingRows={teamSettingRows}
                setTeamSettingRows={setTeamSettingRows}
                clientAssignmentRows={clientAssignmentRows}
                setClientAssignmentRows={setClientAssignmentRows}
                tradePartner={tradePartner}
                setSubClientList={setSubClientList}
                dialogOpen={dialogOpen}
                setDialogOpen={setDialogOpen}
                selectedId={selectedId}
                rowSelectionModel={rowSelectionModel}
                setRowSelectionModel={setRowSelectionModel}
            />

            {/* Subclient Dialog (only for client type) */}
            {type === 'client' && (
                <ClientDialog
                    type="subclient"
                    tradePartner={tradePartner}
                    clientAssignmentRows={clientAssignmentRows}
                    setClientAssignmentRows={setClientAssignmentRows}
                    dialogOpen={subClientDialogOpen}
                    setDialogOpen={setSubClientDialogOpen}
                    subClientList={subClientList}
                    setSubClientList={setSubClientList}
                    subClientRows={subClientRows}
                    setSubClientRows={setSubClientRows}
                />
            )}
        </Box>
    );
}
